// Client-safe permission functions
// These can be used in client components without importing server-only code

import { authModel } from '@/models/authModel';
import { permissionModel } from '@/models/permissionModel';
import { ensureClient } from '@/models/util';
import { SupabaseClient } from '@supabase/supabase-js';
import { Database } from '@/types/supabase';

export type Permission =
  | 'public'
  | 'authenticated'
  | 'admin'
  | 'location-admin'
  | 'event-admin';

export interface UserPermissions {
  userId: string;
  email: string;
  permissions: Permission[];
  federationAdminRoles: Array<{ federation_id: string }>;
  locationAdminRoles: Array<{ location_id: string; federation_id: string }>;
  eventAdminRoles: Array<{ event_id: string }>;
}

/**
 * Get user permissions for client-side components
 * This function handles authentication and permission fetching in one place
 */
export async function getUserPermissions(): Promise<UserPermissions | null> {
  try {
    const session = await authModel.getSession();
    if (!session?.user?.email) {
      return null;
    }

    // Get all permissions from the permission model
    const client = ensureClient();
    const userPermissions = await permissionModel.getPermissions(client, session.user.id);

    // Build permissions array
    const permissions: Permission[] = ['public', 'authenticated'];

    // Check if user is federation admin
    if (userPermissions.federationAdminRoles.length > 0) {
      permissions.push('admin');
    }

    // Check if user is location admin
    if (userPermissions.locationAdminRoles.length > 0) {
      permissions.push('location-admin');
    }

    // Check if user is event admin
    if (userPermissions.eventAdminRoles.length > 0) {
      permissions.push('event-admin');
    }

    return {
      userId: session.user.id,
      email: session.user.email,
      permissions,
      ...userPermissions,
    };
  } catch (error) {
    console.error('Error getting user permissions:', error);
    return null;
  }
}

/**
 * Get user permissions including referee roles for specific use cases
 * This is separate from the main permissions system to avoid giving referees too much access
 */
export async function getUserPermissionsIncludingReferees(
  supabase: SupabaseClient<Database>,
): Promise<{
  userId: string;
  email: string;
  permissions: Permission[];
  federationAdminRoles: Array<{ federation_id: string }>;
  locationAdminRoles: Array<{ location_id: string; federation_id: string }>;
  eventAdminRoles: Array<{ event_id: string }>;
} | null> {
  try {
    // Get user from Supabase auth (works for both client and server contexts)
    const { data: auth, error: authErr } = await supabase.auth.getUser();
    if (authErr || !auth.user?.id || !auth.user?.email) {
      return null;
    }

    // Get all permissions from the permission model
    const userPermissions = await permissionModel.getPermissions(supabase, auth.user.id);

    // Get event admin roles including referees
    const eventAdminRolesIncludingReferees =
      await permissionModel.getEventAdminRolesIncludingReferees(supabase, auth.user.id);

    // Build permissions array
    const permissions: Permission[] = ['public', 'authenticated'];

    // Check if user is federation admin
    if (userPermissions.federationAdminRoles.length > 0) {
      permissions.push('admin');
    }

    // Check if user is location admin
    if (userPermissions.locationAdminRoles.length > 0) {
      permissions.push('location-admin');
    }

    // Check if user is event admin (only actual admins, not referees)
    if (userPermissions.eventAdminRoles.length > 0) {
      permissions.push('event-admin');
    }

    return {
      userId: auth.user.id,
      email: auth.user.email,
      permissions,
      federationAdminRoles: userPermissions.federationAdminRoles,
      locationAdminRoles: userPermissions.locationAdminRoles,
      eventAdminRoles: eventAdminRolesIncludingReferees.map((role) => ({
        event_id: role.event_id,
      })),
    };
  } catch (error) {
    console.error('Error getting user permissions including referees:', error);
    return null;
  }
}

/**
 * Check if user has required permissions
 */
export function hasPermission(
  userPermissions: Permission[] | UserPermissions | null,
  requiredPermissions?: Permission[],
): boolean {
  if (!userPermissions) return false;

  // Handle both Permission[] and UserPermissions types
  const permissions = Array.isArray(userPermissions)
    ? userPermissions
    : userPermissions.permissions;

  // If no permissions required, item is public
  if (!requiredPermissions || requiredPermissions.length === 0) {
    return true;
  }

  // Check if user has at least one of the required permissions
  return requiredPermissions.some((permission) => permissions.includes(permission));
}

/**
 * Check if user has all of the specified permissions
 */
export function hasAllPermissions(
  userPermissions: Permission[] | UserPermissions | null,
  requiredPermissions: Permission[],
): boolean {
  if (!userPermissions) return false;

  // Handle both Permission[] and UserPermissions types
  const permissions = Array.isArray(userPermissions)
    ? userPermissions
    : userPermissions.permissions;

  return requiredPermissions.every((permission) => permissions.includes(permission));
}

/**
 * Get the highest permission level for a user
 */
export function getHighestPermission(permissions: Permission[]): Permission {
  if (permissions.includes('admin')) return 'admin';
  if (permissions.includes('location-admin')) return 'location-admin';
  if (permissions.includes('authenticated')) return 'authenticated';
  return 'public';
}

/**
 * Check if a permission level meets or exceeds another.
 */
export function permissionMeetsLevel(
  userPermission: Permission,
  requiredLevel: Permission,
): boolean {
  const levels: Record<Permission, number> = {
    public: 0,
    authenticated: 1,
    'event-admin': 2,
    'location-admin': 2,
    admin: 3,
  };

  return levels[userPermission] >= levels[requiredLevel];
}
